import React, { useCallback, useEffect, useState } from "react";
import { authToken } from "../../lib/api";
import { normalizeVrUrl, buildVrEditPatch } from "../../lib/vrLinks";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { ChevronDown, Film, Headset, Image as ImageIcon, Link2, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// 与后端 eng_vr.py 的 VR_LINK_ADD_ROLES / VR_LINK_DELETE_ROLES 对齐：
// 2026-10-22 起「能加的人就能改能删」，所有现场参与人（设计侧角色也含在内）一视同仁。
const LINK_ROLES = ["admin", "ceo", "eng_manager", "pm", "cdc", "member", "design_manager", "cn_architect", "us_policy"];
const ADD_ROLES = LINK_ROLES;
const DEL_ROLES = LINK_ROLES;

const KIND = {
  link: { icon: Link2, zh: "链接", en: "Link" },
  video: { icon: Film, zh: "视频", en: "Video" },
  photo: { icon: ImageIcon, zh: "全景照片", en: "360 photo" },
};

// 条目 → 可跳转 URL：外部链接直接用 url；上传件走文件下载口（带 token，可在新标签页打开）
// ⚠️ token 一律走 `authToken()`（存储 → axios 实头回退），不要直接读 localStorage：
//    存储里可能没有 token（仅本次会话登录 / 存储被清）→ 拼出 `?auth=` 空串会被 Chrome ORB 拦成破图。
const itemUrl = (it) => {
  if (!it) return "";
  if (it.kind === "link") return it.url || "";
  if (!it.fileId) return "";
  const tok = authToken();
  return `${BACKEND}/api/files/${it.fileId}/download?auth=${encodeURIComponent(tok)}`;
};
const itemTitle = (it) => it?.title || it?.filename || it?.url || "-";
const itemHost = (it) => {
  try { return new URL(it?.url || "").host.replace(/^www\./, ""); } catch { return ""; }
};

/** 链接补全协议：没写 http(s):// 的补 https://，空串原样返回。 */
export { normalizeVrUrl, buildVrEditPatch } from "../../lib/vrLinks";

/**
 * VrLinkButton —— 项目详情页头部的「VR」分体按钮（左侧主按钮 + 右侧下拉，一体式）。
 *
 * 数据源：`GET /projects/{id}/engine/vr`（集合 engine_vr，360 VR 现场全景：链接 / 视频 / 全景照片）。
 *   · 有链接 → 点主按钮直接在新标签页打开【最新一条】；
 *   · 无链接 → 点主按钮弹出「填写视频链接」输入框（输入框自动聚焦，回车即存）；
 *   · 下拉 → 列出全部条目（点任意一条新开标签页跳转）；底部常驻「添加视频链接」按钮；
 *   · 每条右侧 ✏️ 编辑 / ✕ 删除（权限与后端 vr_update / vr_delete 一致）。
 * 全部跳转一律 window.open(..., "_blank") 新标签页。
 */
export default function VrLinkButton({ api, id, zh, user }) {
  const [items, setItems] = useState(null);   // null = 加载中
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState("");
  // 编辑态：editId = 正在编辑的条目 id（空串 = 不在编辑）
  const [editId, setEditId] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");

  const canAdd = ADD_ROLES.includes(user?.role);
  const canDel = DEL_ROLES.includes(user?.role);
  const canEdit = ADD_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}/engine/vr`); setItems(data.items || []); }
    catch { setItems([]); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  // 点击外部 / Esc 关闭由 Radix Popover 负责（见下方 <Popover>），这里不再手挂 document 监听：
  // 原先的 mousedown 判定只认 wrapRef 内的点击，配合 absolute 面板可以工作，
  // 但换成 Portal 渲染后面板不在 wrapRef 子树里，手写监听会把浮层内的点击也当成“点外部”而误关。
  const closePanel = useCallback(() => { setOpen(false); setAdding(false); setEditId(""); }, []);

  const jump = (u) => { if (u) window.open(u, "_blank", "noopener"); };

  const onMain = () => {
    const latest = (items || []).map(itemUrl).find(Boolean);
    if (latest) { jump(latest); return; }                 // 有链接：直接跳最新一条
    if (!canAdd) { toast.error(zh ? "暂无视频链接" : "No video link yet"); return; }
    setOpen(true); setAdding(true);                      // 无链接：弹填写框
  };

  const submit = async () => {
    const full = normalizeVrUrl(url);
    if (!full) { toast.error(zh ? "请填写视频链接" : "Link required"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${id}/engine/vr/links`, { url: full });
      toast.success(zh ? "视频链接已添加" : "Video link added");
      setUrl(""); setAdding(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "添加失败" : "Failed to add")); }
    setBusy(false);
  };

  const del = async (it) => {
    if (!window.confirm(zh ? `删除「${itemTitle(it)}」？` : `Delete "${itemTitle(it)}"?`)) return;
    setBusyId(it.id);
    try { await api.delete(`/projects/${id}/engine/vr/${it.id}`); toast.success(zh ? "已删除" : "Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "删除失败" : "Failed to delete")); }
    setBusyId("");
  };

  // 编辑一条：标题必填（可空），链接只有 kind=link 才能改（上传件改链接会和文件对不上）
  const startEdit = (it) => {
    setEditId(it.id);
    setEditTitle(it?.title || "");
    setEditUrl(it?.url || "");
  };
  const cancelEdit = () => { setEditId(""); setEditTitle(""); setEditUrl(""); };

  const saveEdit = async (it) => {
    const { patch, error } = buildVrEditPatch(it, { title: editTitle, url: editUrl });
    if (error) { toast.error(zh ? "请填写视频链接" : "Link required"); return; }
    setBusyId(it.id);
    try {
      await api.patch(`/projects/${id}/engine/vr/${it.id}`, patch);
      toast.success(zh ? "已保存" : "Saved");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed to save"));
    }
    setBusyId("");
  };

  const count = (items || []).length;

  return (
    // 浮层由 Radix Popover 渲染到 Portal：它自带视口碰撞回避（会按可用宽度翻转/夹取），
    // 手机窄屏下不再像原 `absolute right-0 w-80` 那样从按钮向左溢出屏幕、把输入框顶到屏幕外。
    <Popover
      open={open}
      // 面板生命周期统一在这里收口：关闭时清空「填写中 / 编辑中」临时状态；
      // 打开时回到列表态，避免上次残留的输入框直接顶在面板顶部。
      onOpenChange={(next) => { setOpen(next); if (next) { setAdding(false); setEditId(""); } else closePanel(); }}>
      <div className="inline-flex shrink-0" data-testid="hub-vr-btn">
        <button type="button" onClick={onMain} data-testid="hub-vr-main"
          title={count ? (zh ? "打开最新视频链接（新标签页）" : "Open the latest video link (new tab)")
            : (canAdd ? (zh ? "添加视频链接" : "Add a video link") : (zh ? "暂无视频链接" : "No video link yet"))}
          className="h-7 inline-flex items-center gap-1 rounded-l-lg bg-indigo-600 pl-2 pr-1.5 text-xs font-bold text-white hover:bg-indigo-700 transition-colors">
          <Headset className="h-3.5 w-3.5" />VR
          {count > 1 && <span className="ml-0.5 rounded-full bg-white/25 px-1 text-[10px] font-bold leading-4" data-testid="hub-vr-count">{count}</span>}
        </button>
        {/* 下拉箭头必须是 PopoverTrigger：若自己 onClick 翻转 open，
            Radix 会先把这次点击判成「点外部」关掉面板，随即又因 onClick 设置成打开，面板就永远关不掉。 */}
        <PopoverTrigger asChild>
          <button type="button" data-testid="hub-vr-caret"
            title={zh ? "视频链接列表" : "Video links"}
            className="h-7 inline-flex items-center rounded-r-lg bg-indigo-600 pl-1 pr-1.5 text-white border-l border-white/25 hover:bg-indigo-700 transition-colors">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        // align="end"：桌面保持“面板右缘对齐按钮”的原视觉（市检入口同样用 Popover 承载浮层）。
        // 手机 390px 下 Radix 会按可用宽度把面板夹回视口内，不再像原 `absolute right-0 w-80` 那样
        // 从按钮向左溢出、把输入框顶到屏幕外；collisionPadding 保证夹回后仍留出边距，不贴屏幕边。
        align="end"
        sideOffset={6}
        collisionPadding={12}
        // 需要输入框的浮层不能自动抢焦点到第一个可聚焦项；这里交由 input autoFocus 显式控制。
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[min(20rem,calc(100vw-1.5rem))] rounded-xl border border-slate-100 bg-white p-2 shadow-xl"
        data-testid="hub-vr-panel">
        {adding ? (
          <div className="space-y-2">
            <p className="text-[11px] text-slate-500">{zh ? "粘贴视频链接（B 站 / 抖音 / YouTube / Vimeo / 网盘 / VR 平台均可）" : "Paste a video link"}</p>
            <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder="https://..."
              data-testid="hub-vr-input"
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700 focus:border-indigo-400 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setAdding(false); setUrl(""); }} data-testid="hub-vr-cancel"
                className="h-7 rounded-lg border border-slate-200 px-2.5 text-xs text-slate-600 hover:bg-slate-50">{zh ? "取消" : "Cancel"}</button>
              <button type="button" onClick={submit} disabled={busy} data-testid="hub-vr-save"
                className="h-7 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}{zh ? "保存" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="max-h-64 overflow-y-auto" data-testid="hub-vr-list">
              {items === null ? (
                <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>
              ) : items.length === 0 ? (
                <p className="px-2 py-4 text-center text-[11px] text-slate-400" data-testid="hub-vr-empty">{zh ? "暂无视频链接" : "No video link yet"}</p>
              ) : items.map((it, i) => {
                const k = KIND[it.kind] || KIND.link;
                const Icon = k.icon;
                // 编辑态：这一条展开成表单（标题 + 链接），保存走 PATCH
                if (editId === it.id) {
                  return (
                    <div key={it.id} className="space-y-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 px-2 py-2" data-testid={`hub-vr-edit-${it.id}`}>
                      <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder={zh ? "标题（可空）" : "Title (optional)"}
                        data-testid={`hub-vr-edit-title-${it.id}`}
                        className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:outline-none" />
                      <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} disabled={it.kind !== "link"} placeholder="https://..."
                        data-testid={`hub-vr-edit-url-${it.id}`}
                        className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400" />
                      <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={cancelEdit} data-testid={`hub-vr-edit-cancel-${it.id}`}
                          className="h-6 inline-flex items-center gap-0.5 rounded-lg border border-slate-200 px-2 text-[11px] text-slate-600 hover:bg-slate-50">
                          <X className="h-3 w-3" />{zh ? "取消" : "Cancel"}
                        </button>
                        <button type="button" onClick={() => saveEdit(it)} disabled={busyId === it.id} data-testid={`hub-vr-edit-save-${it.id}`}
                          className="h-6 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                          {busyId === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}{zh ? "保存" : "Save"}
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={it.id} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-slate-50" data-testid={`hub-vr-item-${it.id}`}>
                    <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <button type="button" onClick={() => { jump(itemUrl(it)); setOpen(false); }} title={it.url || it.filename || ""}
                      className="min-w-0 flex-1 text-left" data-testid={`hub-vr-open-${it.id}`}>
                      <span className="block truncate text-xs font-medium text-slate-700">{itemTitle(it)}</span>
                      <span className="block truncate text-[10px] text-slate-400">
                        {/* 长域名可能截断行尾日期；日期放在最前面，缺日期时不显示多余分隔符。 */}
                        {it.at ? `${String(it.at).slice(0, 10)} · ` : ""}{i === 0 ? (zh ? "最新 · " : "Latest · ") : ""}{zh ? k.zh : k.en}{itemHost(it) ? ` · ${itemHost(it)}` : ""}
                      </span>
                    </button>
                    {canEdit && (
                      <button type="button" onClick={() => startEdit(it)} title={zh ? "编辑" : "Edit"} data-testid={`hub-vr-edit-btn-${it.id}`}
                        className="shrink-0 text-slate-300 hover:text-indigo-600 transition-colors">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canDel && (
                      <button type="button" onClick={() => del(it)} title={zh ? "删除" : "Delete"} data-testid={`hub-vr-del-${it.id}`}
                        className="shrink-0 text-slate-300 hover:text-red-600 transition-colors">
                        {busyId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {canAdd && (
              <div className="sticky bottom-0 mt-1.5 border-t border-slate-100 bg-white pt-1.5">
                <button type="button" onClick={() => setAdding(true)} data-testid="hub-vr-add"
                  className="w-full h-7 inline-flex items-center justify-center gap-1 rounded-lg border border-dashed border-indigo-300 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors">
                  <Plus className="h-3.5 w-3.5" />{zh ? "添加视频链接" : "Add video link"}
                </button>
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
